// Easter eggs: little secrets hidden around the gallery.
//
//  1. konami     ↑ ↑ ↓ ↓ ← → ← → B A: the paintings sway and confetti falls from the glass roof
//  2. wish       click a shooting star at night: a sparkle, and your wish is kept safe
//  3. horse      click the golden horse five times: it rears up and neighs
//  4. sketch     stand still in the Grand Salon at night: an early sketch appears on a wall
//  5. deer       stand still at a window: a curious deer walks up to the glass to see you
//  6. birthday   on the artist's birthday: balloons drift past the windows (preview: ?celebrate)
//  7. paint      type "paint" anywhere: a splash of paint and a quote about art
//  8. signature  in a storm, each lightning flash reveals the artist's signature on a painting
//  9. owl        visit between 2 and 4 am: an owl keeps you company on the glass roof
// 10. fireworks  the gallery's 1,000th like (and every 1,000 after): fireworks over the mountains
//               (preview: ?fireworks)
// 11. rainbow    when heavy rain clears into a bright day: a rainbow over the mountains
// 12. midnight   when the wall clock strikes midnight: twelve soft bells and the picture lights pulse
//               (preview: ?midnight sets the clock to a minute before)
// 13. meteors    on the nights of real meteor showers (Perseids, Geminids…): many more shooting stars
//               (preview: ?meteors)
// 14. fullmoon   the moon shows its real phase; on full-moon nights a wolf howls in the distance
//               (preview: ?fullmoon)
//
// Festivals from around the world (lib/festivals.js) decorate the gallery on their days too.
//
// Found secrets are remembered in this browser only (localStorage), to show "Secret 3 of 14".

export const EGG_IDS = ['konami', 'wish', 'horse', 'sketch', 'deer', 'birthday', 'paint', 'signature', 'owl', 'fireworks', 'rainbow', 'midnight', 'meteors', 'fullmoon'];

// The artist's birthday as 'MM-DD' (e.g. '07-21'). null switches the birthday surprise off.
export const ARTIST_BIRTHDAY = '06-07'; // David's birthday, June 7

// Shared, frame-by-frame state for the 3D scene (read in useFrame; no React re-renders).
export const eggState = {
  konamiUntil: 0,    // performance.now() until which the paintings sway and confetti falls
  fireworksUntil: 0, // … fireworks over the mountains
  celebrate: false,  // birthday balloons
  owl: false,        // the night owl on the roof
  burst: null,       // { x, y, z, at }: gold sparkles where a shooting star was caught
  chimeAt: 0,        // performance.now() of the last midnight chime (picture lights pulse)
  confettiUntil: 0,      // festival petals / confetti falling (without the paintings dancing)
  confettiColors: null,  // their colours (THREE.Color[])
  fireworksColors: null, // festival palette for fireworks (THREE.Color[]), null = the usual mix
  balloonColors: null,   // festival palette for balloons (THREE.Color[])
};

const preview = (name) => typeof window !== 'undefined' && new RegExp(`[?&]${name}\\b`).test(window.location.search);

// Moon phase from 0 (new) through 0.5 (full) back to 1, from a known new moon (6 Jan 2000 18:14 UTC).
export const moonPhase = (date = new Date()) => {
  if (preview('fullmoon')) return 0.5;
  const synodic = 29.530588853;
  const days = (date.getTime() - Date.UTC(2000, 0, 6, 18, 14)) / 86400000;
  return (((days / synodic) % 1) + 1) % 1;
};
export const isFullMoon = (date = new Date()) => Math.abs(moonPhase(date) - 0.5) < 0.034; // about a day either side

// Peak nights of the major annual meteor showers ([month, first day, last day]).
const METEOR_SHOWERS = [
  ['Quadrantids', 1, 2, 4], ['Lyrids', 4, 21, 23], ['Eta Aquariids', 5, 5, 7], ['Perseids', 8, 11, 13],
  ['Orionids', 10, 20, 22], ['Leonids', 11, 16, 18], ['Geminids', 12, 13, 15],
];
export const meteorShowerTonight = (date = new Date()) => {
  if (preview('meteors')) return 'Perseids';
  const m = date.getMonth() + 1, d = date.getDate();
  const hit = METEOR_SHOWERS.find(([, mm, a, b]) => mm === m && d >= a && d <= b);
  return hit ? hit[0] : null;
};
export const isMidnightPreview = () => preview('midnight');

const found = () => {
  try { return JSON.parse(localStorage.getItem('gallery-secrets') || '[]'); } catch (e) { return []; }
};

// Show a message and count the secret as found.
export const announceEgg = (id, message, icon = '✨') => {
  const list = found();
  if (!list.includes(id)) {
    list.push(id);
    try { localStorage.setItem('gallery-secrets', JSON.stringify(list)); } catch (e) { /* ignore */ }
  }
  window.dispatchEvent(new CustomEvent('gallery-egg', { detail: { id, message, icon, found: list.length, total: EGG_IDS.length } }));
};

export const isBirthdayToday = (date = new Date()) => {
  if (typeof window !== 'undefined' && /[?&]celebrate\b/.test(window.location.search)) return true;
  if (!ARTIST_BIRTHDAY) return false;
  const mmdd = `${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
  return mmdd === ARTIST_BIRTHDAY;
};

// Real local time between 2 and 4 am.
export const isOwlHour = (date = new Date()) => {
  const h = date.getHours();
  return h >= 2 && h < 4;
};

// Quotes for the "paint" secret.
export const ART_QUOTES = [
  { text: 'I dream my painting and I paint my dream.', by: 'Vincent van Gogh' },
  { text: 'Every child is an artist. The problem is how to remain an artist once we grow up.', by: 'Pablo Picasso' },
  { text: 'Color is my day-long obsession, joy and torment.', by: 'Claude Monet' },
  { text: 'Art enables us to find ourselves and lose ourselves at the same time.', by: 'Thomas Merton' },
  { text: 'The painter has the Universe in his mind and hands.', by: 'Leonardo da Vinci' },
  { text: 'Creativity takes courage.', by: 'Henri Matisse' },
];

// Tiny synthesized sounds (Web Audio, no files). They only play after the visitor has interacted.
let audio = null;
const ctx = () => {
  try {
    const AC = window.AudioContext || window.webkitAudioContext;
    if (!AC) return null;
    if (!audio) audio = new AC();
    if (audio.state !== 'running') audio.resume().catch(() => {});
    return audio;
  } catch (e) { return null; }
};

// A horse's whinny: a vibrating, falling squeal through a vocal-like filter, then snorts of breath.
export const playNeigh = () => {
  const ac = ctx();
  if (!ac) return;
  const t = ac.currentTime + 0.05;
  const osc = ac.createOscillator();
  osc.type = 'sawtooth';
  osc.frequency.setValueAtTime(650, t);
  osc.frequency.linearRampToValueAtTime(1150, t + 0.25);
  osc.frequency.linearRampToValueAtTime(900, t + 0.7);
  osc.frequency.exponentialRampToValueAtTime(380, t + 1.4);
  const vib = ac.createOscillator();
  vib.frequency.value = 22; // the horse's quaver
  const vibAmt = ac.createGain();
  vibAmt.gain.value = 55;
  vib.connect(vibAmt).connect(osc.frequency);
  const bp = ac.createBiquadFilter();
  bp.type = 'bandpass'; bp.frequency.value = 1400; bp.Q.value = 1.4;
  const g = ac.createGain();
  g.gain.setValueAtTime(0, t);
  g.gain.linearRampToValueAtTime(0.16, t + 0.06);
  g.gain.setValueAtTime(0.16, t + 0.9);
  g.gain.exponentialRampToValueAtTime(0.001, t + 1.5);
  osc.connect(bp).connect(g).connect(ac.destination);
  osc.start(t); vib.start(t); osc.stop(t + 1.6); vib.stop(t + 1.6);
  // hoof clops as it lands
  [1.55, 1.75].forEach((dt) => {
    const o = ac.createOscillator();
    o.type = 'triangle';
    o.frequency.setValueAtTime(180, t + dt);
    o.frequency.exponentialRampToValueAtTime(60, t + dt + 0.08);
    const gg = ac.createGain();
    gg.gain.setValueAtTime(0.25, t + dt);
    gg.gain.exponentialRampToValueAtTime(0.001, t + dt + 0.12);
    o.connect(gg).connect(ac.destination);
    o.start(t + dt); o.stop(t + dt + 0.15);
  });
};

// A soft magical chime (wishes, secrets).
export const playChime = () => {
  const ac = ctx();
  if (!ac) return;
  const t = ac.currentTime + 0.02;
  [1046.5, 1318.5, 1568, 2093].forEach((f, i) => {
    const o = ac.createOscillator();
    o.type = 'sine';
    o.frequency.value = f;
    const g = ac.createGain();
    g.gain.setValueAtTime(0, t + i * 0.09);
    g.gain.linearRampToValueAtTime(0.07, t + i * 0.09 + 0.02);
    g.gain.exponentialRampToValueAtTime(0.0005, t + i * 0.09 + 1.2);
    o.connect(g).connect(ac.destination);
    o.start(t + i * 0.09); o.stop(t + i * 0.09 + 1.3);
  });
};

// Distant firework pops.
export const playPops = (count = 8, overSeconds = 7) => {
  const ac = ctx();
  if (!ac) return;
  for (let i = 0; i < count; i++) {
    const t = ac.currentTime + 0.3 + (i / count) * overSeconds + Math.random() * 0.4;
    const len = Math.floor(ac.sampleRate * 0.4);
    const buf = ac.createBuffer(1, len, ac.sampleRate);
    const d = buf.getChannelData(0);
    for (let k = 0; k < len; k++) d[k] = (Math.random() * 2 - 1) * Math.pow(1 - k / len, 4);
    const src = ac.createBufferSource();
    src.buffer = buf;
    const lp = ac.createBiquadFilter();
    lp.type = 'lowpass'; lp.frequency.value = 900;
    const g = ac.createGain();
    g.gain.value = 0.35;
    src.connect(lp).connect(g).connect(ac.destination);
    src.start(t);
  }
};

// Twelve soft bells, for midnight.
export const playBells = (count = 12) => {
  const ac = ctx();
  if (!ac) return;
  for (let i = 0; i < count; i++) {
    const t = ac.currentTime + 0.1 + i * 1.6;
    [1, 2.76, 5.4].forEach((h, k) => { // a bell's inharmonic partials
      const o = ac.createOscillator();
      o.type = 'sine';
      o.frequency.value = 196 * h;
      const g = ac.createGain();
      g.gain.setValueAtTime(0, t);
      g.gain.linearRampToValueAtTime(0.09 / (k + 1), t + 0.01);
      g.gain.exponentialRampToValueAtTime(0.0005, t + 2.4 / (k + 1));
      o.connect(g).connect(ac.destination);
      o.start(t); o.stop(t + 2.5);
    });
  }
};

// A wolf's howl far away: a slow rise, a long wavering note and a fall, softened by distance.
export const playHowl = () => {
  const ac = ctx();
  if (!ac) return;
  const t = ac.currentTime + 0.05;
  const o = ac.createOscillator();
  o.type = 'sawtooth';
  o.frequency.setValueAtTime(330, t);
  o.frequency.exponentialRampToValueAtTime(560, t + 0.9);
  o.frequency.setValueAtTime(560, t + 2.2);
  o.frequency.exponentialRampToValueAtTime(380, t + 3.4);
  const vib = ac.createOscillator();
  vib.frequency.value = 5;
  const vibAmt = ac.createGain();
  vibAmt.gain.value = 7;
  vib.connect(vibAmt).connect(o.frequency);
  const lp = ac.createBiquadFilter();
  lp.type = 'lowpass'; lp.frequency.value = 900; lp.Q.value = 4;
  const g = ac.createGain();
  g.gain.setValueAtTime(0, t);
  g.gain.linearRampToValueAtTime(0.06, t + 0.6);
  g.gain.setValueAtTime(0.06, t + 2.6);
  g.gain.exponentialRampToValueAtTime(0.0005, t + 3.6);
  o.connect(lp).connect(g).connect(ac.destination);
  o.start(t); vib.start(t); o.stop(t + 3.7); vib.stop(t + 3.7);
};
